/**
 * Les trois fins de victoire — SPEC-MOTEUR §9.4, PLAN §4.13.
 *
 * *« Les fins de victoire (1M) : La Rue, CEO, La Communauté — déterminées par le build et
 * l'image de chaîne, pas par un choix de menu : c'est ta façon de jouer qui écrit ta fin. »*
 * (PLAN §4.13)
 *
 * ── Ce que ce module débloque ──────────────────────────────────────────────────
 *
 * `state.ending` n'était **écrit nulle part** dans le dépôt, et ce n'est pas un champ
 * décoratif : c'est une **garde**. `server/src/progression.ts` fait sortir `graverVictoire`
 * si `!etat.ending`. Donc, toujours : aucun `victoryEnding/Turn/Score` écrit, **aucune ligne
 * de `leaderboard` jamais insérée**, jamais de `won_continuing`, `progression.victoire`
 * toujours `null`, `decomposerScoreVictoire` jamais atteint, et l'écran de fin du client
 * (`Fins/Fin.tsx`, `state.ending ? FINS[state.ending] : null`) **toujours vide**. Toute la fin
 * du jeu tenait à cette ligne manquante.
 *
 * ── L'ordre d'évaluation, et ce qu'il implique vraiment ────────────────────────
 *
 * §9.4 fixe **Rue > Communauté > CEO**, la première condition vraie gagne, CEO par défaut.
 * ⚠️ **Conséquence non évidente, mesurée** : dans `content/skills.json`, *Figure publique*
 * exige transitivement *La Catchphrase*
 * (`intro_signature > gestion_d_image > reponses_commentaires > la_catchphrase > le_live >
 * gestion_de_crise > figure_publique`). Tout build qui remplit **La Communauté** remplit donc
 * déjà la moitié de **La Rue**, et son image ≥ +40 satisfait aussi l'image ≥ 0 de La Rue.
 *
 * **La Communauté n'est donc atteignable qu'avec moins de 2 jalons réussis.** C'est cohérent —
 * La Rue est la fin « du village », elle se mérite par les grands rendez-vous ; La Communauté
 * est la fin de celui qui a construit un lien plutôt qu'un événement — mais c'est une
 * propriété **émergente de l'arbre**, pas une intention écrite. Signalée à Mathieu ; l'ordre
 * de §9.4 porte lui-même un ✎ et ne bouge pas de ma main (CLAUDE.md : la spec gagne sur mon
 * intuition).
 *
 * ── « Contenu = données » : l'entorse, assumée et nommée ───────────────────────
 *
 * Les quatre nœuds ci-dessous sont lus **par id**. C'est une entorse à la règle, la même
 * qu'⟦A35⟧ a déjà assumée pour *Figure publique* dans `pause.ts` : il n'existe aucun champ de
 * contenu pour décrire une condition de fin, et en inventer un serait une modification de
 * SPEC-CONTENU — hors périmètre d'une session de moteur. Les ids sont donc **des constantes
 * nommées, exportées et testées contre le contenu réel** : si l'un disparaissait de
 * `content/skills.json`, un test rougirait au lieu qu'une fin devienne silencieusement
 * inatteignable.
 *
 * Pureté (CLAUDE.md) : aucune I/O, aucune horloge système, aucun `Math.random`. La fin se
 * **lit** dans l'état, elle ne se tire pas.
 */
import { NOEUD_FIGURE_PUBLIQUE } from "./fil.js";
import { SYSTEMES_TOUS_ACTIFS } from "./menage.js";
// ─────────────────────────────────────────────────────────────────────────────
// Les conditions, en constantes nommées (SPEC-MOTEUR §9.4, PLAN §4.13)
// ─────────────────────────────────────────────────────────────────────────────
/** Nœud *La Catchphrase* — la voie Charisme/Algorithme de **La Rue**. */
export const NOEUD_CATCHPHRASE = "la_catchphrase";
/** Nœud *Lore de chaîne* — la voie Écriture de **La Communauté**. */
export const NOEUD_LORE_DE_CHAINE = "lore_de_chaine";
// Nœud *Figure publique* : **importé de `fil.ts`**, qui le lit par id pour la décroissance de
// Hype ⟦A-57⟧ — et `pause.ts` l'y lit aussi depuis M3.21, pour son bonus de retour ⟦A35⟧.
// Deux constantes pour le même nœud, c'est deux endroits où une faute de frappe rend une
// mécanique silencieusement inerte — l'entorse à « contenu = données » se paie une fois.
/** Nœud *L'Empire* — la voie Business de **CEO**. */
export const NOEUD_EMPIRE = "l_empire";
/** **La Rue** : jalons réussis exigés (PLAN §4.13). */
export const RUE_JALONS_MIN = 2;
/**
 * **La Rue** : image de chaîne minimale — *« un village ne baptise pas une rue au nom d'un
 * polémiste »* (PLAN §4.7).
 */
export const RUE_IMAGE_MIN = 0;
/** **La Communauté** : image de chaîne minimale (PLAN §4.7). */
export const COMMUNAUTE_IMAGE_MIN = 40;
/** Les ids de nœuds dont dépend une fin — surface de test contre le contenu réel. */
export const NOEUDS_DE_FIN = [
    NOEUD_CATCHPHRASE,
    NOEUD_LORE_DE_CHAINE,
    NOEUD_FIGURE_PUBLIQUE,
    NOEUD_EMPIRE,
];
// ─────────────────────────────────────────────────────────────────────────────
// La détermination
// ─────────────────────────────────────────────────────────────────────────────
/** Le joueur possède-t-il ce nœud ? */
function possede(state, skillId) {
    return state.skills.includes(skillId);
}
/**
 * Nombre de **jalons réussis** — `jalons_done` ne contient que les réussis (§9.3).
 *
 * ⚠️ ⟦A-22⟧ **C'est ici que la correction du jalon raté aurait pu déraper.** Depuis A-22 un
 * jalon raté est consommé lui aussi, mais il part dans `state.jalons_rates` — **jamais** dans
 * `jalons_done`. Si les deux listes avaient été fondues, La Rue (≥ 2 jalons réussis) se serait
 * ouverte à un joueur qui a **raté** deux vidéos, et la condition de fin de §9.4 aurait changé
 * de sens en silence. « Tenté » ferme le bouton (`jalonDejaTente`) ; « réussi » ouvre la fin.
 */
export function jalonsReussis(state) {
    return state.jalons_done.length;
}
/**
 * Les trois fins et l'état de leurs conditions, **dans l'ordre d'évaluation de §9.4**.
 *
 * Exportée séparément de `determinerFin` parce que c'est ce que l'écran de fin doit montrer :
 * *« voilà pourquoi tu as CETTE fin-là »*. Une fin qui tombe sans qu'on comprenne pourquoi est
 * une fin qu'on n'a pas jouée.
 */
export function conditionsDeFin(state, 
/**
 * ⟦C60 · A-177⟧ Les interrupteurs du ménage. Sans jalons, la condition « ≥ 2 jalons réussis » de
 * La Rue ne pourrait plus jamais être remplie : elle est LEVÉE (la fin ne se mérite plus que
 * par l'arbre). Sans alignement, l'image de chaîne n'existe plus : les conditions d'image sont
 * LEVÉES aussi. Absent ⇒ le jeu d'avant, au caractère près.
 */
sys = SYSTEMES_TOUS_ACTIFS) {
    const image = state.channel_image;
    const jalons = jalonsReussis(state);
    const rueManque = [];
    if (!possede(state, NOEUD_CATCHPHRASE))
        rueManque.push(NOEUD_CATCHPHRASE);
    if (sys.jalons && jalons < RUE_JALONS_MIN)
        rueManque.push(`jalons:${jalons}/${RUE_JALONS_MIN}`);
    if (sys.alignement && image < RUE_IMAGE_MIN)
        rueManque.push(`image:${image}/${RUE_IMAGE_MIN}`);
    const communauteManque = [];
    if (!possede(state, NOEUD_LORE_DE_CHAINE))
        communauteManque.push(NOEUD_LORE_DE_CHAINE);
    if (!possede(state, NOEUD_FIGURE_PUBLIQUE))
        communauteManque.push(NOEUD_FIGURE_PUBLIQUE);
    if (sys.alignement && image < COMMUNAUTE_IMAGE_MIN) {
        communauteManque.push(`image:${image}/${COMMUNAUTE_IMAGE_MIN}`);
    }
    // CEO : *L'Empire* est sa voie, mais §9.4 en fait aussi **la fin par défaut** — elle est
    // donc toujours « remplie » au sens de l'évaluation. `manque` reste vide : il n'y a rien à
    // reprocher à un joueur qui arrive au million sans avoir pris l'une des deux autres voies.
    return [
        { ending: "la_rue", remplie: rueManque.length === 0, manque: rueManque },
        { ending: "la_communaute", remplie: communauteManque.length === 0, manque: communauteManque },
        { ending: "ceo", remplie: true, manque: [] },
    ];
}
/**
 * **La fin de la run.** §9.4 : ordre Rue > Communauté > CEO, la première condition vraie
 * gagne, **CEO par défaut si aucune n'est remplie**.
 *
 * Ne mute rien : la fin est une **lecture** de l'état au moment du million. C'est ce qui rend
 * la gravure honnête — `graverVictoire` fige ce que le joueur avait, pas ce qu'il a fait
 * ensuite (PLAN §4.13 : « rien ne peut plus reprendre la victoire »).
 */
export function determinerFin(state, sys = SYSTEMES_TOUS_ACTIFS) {
    for (const c of conditionsDeFin(state, sys)) {
        if (c.remplie)
            return c.ending;
    }
    // Inatteignable : CEO est toujours `remplie`. Le `return` explicite documente la garantie
    // de §9.4 (« CEO par défaut ») plutôt que de la laisser à un `!` de compilation.
    return "ceo";
}
/**
 * `voiePropre` — le joueur a-t-il **acheté la voie** de la fin qu'il obtient, ou l'a-t-il eue
 * par défaut ? Sert à l'écran de fin (et au récit d'⟦A-20⟧ sur l'arc de rédemption) : « CEO
 * par défaut » et « CEO parce que tu as bâti L'Empire » ne se racontent pas pareil.
 */
export function finMeritee(state, ending) {
    if (ending === "ceo")
        return possede(state, NOEUD_EMPIRE);
    return true; // les deux autres ne s'obtiennent QUE par leurs conditions
}
//# sourceMappingURL=fins.js.map